/**
 * Zustand store for recording UI state.
 *
 * Holds the recording state-machine phase, current cue, take history (per
 * cue), block-mode selection, ref-voice fader settings, and ephemeral
 * banner / modal flags. The actual audio capture and upload pipeline live
 * in `useRecorder` / `useRecordingMachine` and dispatch into this store.
 *
 * The state lives client-side only — server state (cues, projects, takes
 * delivered) belongs in TanStack Query caches under `lib/api/`.
 */
import { create } from 'zustand'

/** Recording state-machine phases (PRD §10). */
export type Phase =
  | 'idle'
  | 'preload'
  | 'pre_roll'
  | 'three_pop'
  | 'recording'
  | 'qc_local'
  | 'upload'
  | 'qc_server'
  | 'review'
  | 'advance'

/** Block-recording grouping mode. `null` ⇒ single-cue. */
export type BlockMode = 'single' | 'auto' | 'scene' | 'range' | 'effort' | null

/** One in-memory take record. Mirrors @ohear/audio TakeRecord but lighter. */
export interface TakeRecord {
  takeId: string
  cueId: string
  takeNumber: number
  versionNumber: number
  recordedAt: number
  durationSec: number
  peak: number
  /** Local audio object URL for review playback. */
  previewUrl: string | null
  /** Upload state, mirrored from UploadQueue. */
  uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'failed'
  uploadProgress: number
}

/** dB volume bound for ref voice slider. -60 = silent, 0 = unity. */
export const REF_VOICE_MIN_DB = -60
export const REF_VOICE_MAX_DB = 0

const REF_VOICE_LS_KEY = 'ohear.refVoice.v1'

interface RefVoicePersist {
  volumeDb: number
  muted: boolean
}

function loadRefVoicePersist(): RefVoicePersist {
  if (typeof localStorage === 'undefined') return { volumeDb: -6, muted: false }
  try {
    const raw = localStorage.getItem(REF_VOICE_LS_KEY)
    if (!raw) return { volumeDb: -6, muted: false }
    const parsed = JSON.parse(raw) as Partial<RefVoicePersist>
    const volumeDb = clamp(parsed.volumeDb ?? -6, REF_VOICE_MIN_DB, REF_VOICE_MAX_DB)
    const muted = parsed.muted === true
    return { volumeDb, muted }
  } catch {
    return { volumeDb: -6, muted: false }
  }
}

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n
}

function saveRefVoicePersist(p: RefVoicePersist): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(REF_VOICE_LS_KEY, JSON.stringify(p))
  } catch {
    /* no-op — quota or disabled. The default reload-time fallback handles it. */
  }
}

export interface RecordingState {
  phase: Phase
  currentCueId: string | null
  /** Map cueId → ordered list of takes recorded this session. */
  takesByCue: Record<string, TakeRecord[]>
  blockMode: BlockMode
  /** Ordered list of cue ids selected for the active block (when blockMode !== 'single'). */
  blockCueIds: string[]

  /** Ref voice fader (persisted to localStorage). */
  refVoiceVolumeDb: number
  refVoiceMuted: boolean

  /** Live playhead position in seconds (driven by RAF in WaveformTimeline). */
  playheadSec: number

  /** Predictive hint banner visible — derived from current cue's category. */
  predictiveCategoryShown: import('@ohear/shared').CueCategory | null
  /** Reset-gain reminder shown after take advance. Cleared on next phase change. */
  resetReminderText: { zh: string; en: string } | null

  /** Keyboard shortcuts help modal open. */
  keyboardHelpOpen: boolean
  /** Settings drawer (mixer + devices) open. */
  settingsDrawerOpen: boolean

  /** Last error to display in a toast (rotates). */
  lastError: string | null
}

export interface RecordingActions {
  setPhase: (phase: Phase) => void
  setCurrentCue: (cueId: string | null) => void
  pushTake: (take: TakeRecord) => void
  updateTakeUpload: (
    takeId: string,
    patch: Partial<Pick<TakeRecord, 'uploadStatus' | 'uploadProgress'>>,
  ) => void
  setBlockMode: (mode: BlockMode, cueIds?: string[]) => void
  setRefVoiceVolume: (db: number) => void
  toggleRefVoiceMute: () => void
  setPlayhead: (sec: number) => void
  setPredictiveCategory: (cat: import('@ohear/shared').CueCategory | null) => void
  setResetReminder: (text: { zh: string; en: string } | null) => void
  toggleKeyboardHelp: () => void
  toggleSettingsDrawer: () => void
  setError: (msg: string | null) => void
  /** Hard reset — used when navigating away from a project. */
  reset: () => void
}

const initialPersist = loadRefVoicePersist()

const initialState: RecordingState = {
  phase: 'idle',
  currentCueId: null,
  takesByCue: {},
  blockMode: 'single',
  blockCueIds: [],
  refVoiceVolumeDb: initialPersist.volumeDb,
  refVoiceMuted: initialPersist.muted,
  playheadSec: 0,
  predictiveCategoryShown: null,
  resetReminderText: null,
  keyboardHelpOpen: false,
  settingsDrawerOpen: false,
  lastError: null,
}

export const useRecordingStore = create<RecordingState & RecordingActions>((set) => ({
  ...initialState,

  setPhase: (phase) => set({ phase }),
  setCurrentCue: (cueId) =>
    set((s) => ({
      currentCueId: cueId,
      // Moving cue while recording is illegal; defensively reset to idle.
      phase: s.phase === 'recording' || s.phase === 'three_pop' ? 'idle' : s.phase,
    })),

  pushTake: (take) =>
    set((s) => {
      const list = s.takesByCue[take.cueId] ?? []
      return { takesByCue: { ...s.takesByCue, [take.cueId]: [...list, take] } }
    }),

  updateTakeUpload: (takeId, patch) =>
    set((s) => {
      const next: Record<string, TakeRecord[]> = {}
      for (const [cueId, takes] of Object.entries(s.takesByCue)) {
        next[cueId] = takes.map((t) => (t.takeId === takeId ? { ...t, ...patch } : t))
      }
      return { takesByCue: next }
    }),

  setBlockMode: (mode, cueIds) => set({ blockMode: mode, blockCueIds: cueIds ?? [] }),

  setRefVoiceVolume: (db) => {
    const clamped = clamp(db, REF_VOICE_MIN_DB, REF_VOICE_MAX_DB)
    set((s) => {
      saveRefVoicePersist({ volumeDb: clamped, muted: s.refVoiceMuted })
      return { refVoiceVolumeDb: clamped }
    })
  },

  toggleRefVoiceMute: () =>
    set((s) => {
      const muted = !s.refVoiceMuted
      saveRefVoicePersist({ volumeDb: s.refVoiceVolumeDb, muted })
      return { refVoiceMuted: muted }
    }),

  setPlayhead: (sec) => set({ playheadSec: sec }),
  setPredictiveCategory: (cat) => set({ predictiveCategoryShown: cat }),
  setResetReminder: (text) => set({ resetReminderText: text }),

  toggleKeyboardHelp: () => set((s) => ({ keyboardHelpOpen: !s.keyboardHelpOpen })),
  toggleSettingsDrawer: () => set((s) => ({ settingsDrawerOpen: !s.settingsDrawerOpen })),

  setError: (msg) => set({ lastError: msg }),

  reset: () => set(initialState),
}))

/** Helper: compute the next take/version number for a cue given current state. */
export function nextTakeNumber(takes: TakeRecord[] | undefined): {
  takeNumber: number
  versionNumber: number
} {
  if (!takes || takes.length === 0) return { takeNumber: 1, versionNumber: 1 }
  const last = takes[takes.length - 1]
  if (!last) return { takeNumber: 1, versionNumber: 1 }
  return { takeNumber: last.takeNumber + 1, versionNumber: last.versionNumber }
}

/** Helper: bump version (called on Pickup). */
export function nextPickupVersion(takes: TakeRecord[] | undefined): {
  takeNumber: number
  versionNumber: number
} {
  if (!takes || takes.length === 0) return { takeNumber: 1, versionNumber: 1 }
  const lastVersion = Math.max(...takes.map((t) => t.versionNumber))
  return { takeNumber: 1, versionNumber: lastVersion + 1 }
}
